import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConversationFeedbackListDTO,
  MessageFeedbackDTO,
  UpsertMessageFeedbackInput,
} from '@shared/schemas/message-feedback';
import { toast } from 'sonner';
import { getChatErrorMessage } from '../chat-errors';
import { deleteMessageFeedback, listConversationFeedback, putMessageFeedback } from '../services/chat.api';
import { conversationKeys } from './useConversation';

export const messageFeedbackKeys = {
  conversation: (conversationId: string) => [...conversationKeys.detail(conversationId), 'feedback'] as const,
};

type FeedbackChange =
  | { input: UpsertMessageFeedbackInput; messageId: string }
  | { input: null; messageId: string };

function applyChange(list: ConversationFeedbackListDTO | undefined, change: FeedbackChange): ConversationFeedbackListDTO {
  const items = (list?.items ?? []).filter((item) => item.messageId !== change.messageId);
  if (change.input) {
    items.push({ messageId: change.messageId, rating: change.input.rating, reason: change.input.reason ?? null });
  }
  return { items };
}

/** The reader's opinions on the answers of a conversation, saved optimistically. */
export function useMessageFeedback(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = messageFeedbackKeys.conversation(conversationId ?? 'none');
  const feedbackQuery = useQuery({
    enabled: Boolean(conversationId),
    queryFn: () => listConversationFeedback(conversationId!),
    queryKey,
    staleTime: 5 * 60_000,
  });

  const mutation = useMutation<void, unknown, FeedbackChange, { previous?: ConversationFeedbackListDTO }>({
    mutationFn: async (change: FeedbackChange) => {
      if (change.input) {
        await putMessageFeedback(change.messageId, change.input);
      } else {
        await deleteMessageFeedback(change.messageId);
      }
    },
    onError: (error, _change, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
      toast.error(getChatErrorMessage(error));
    },
    onMutate: async (change) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ConversationFeedbackListDTO>(queryKey);
      queryClient.setQueryData(queryKey, applyChange(previous, change));
      return { previous };
    },
  });

  const feedbackByMessageId = useMemo(() => new Map<string, MessageFeedbackDTO>(
    (feedbackQuery.data?.items ?? []).map((item) => [item.messageId, item]),
  ), [feedbackQuery.data]);
  const { mutate } = mutation;
  const clear = useCallback((messageId: string) => mutate({ input: null, messageId }), [mutate]);
  const submit = useCallback(
    (messageId: string, input: UpsertMessageFeedbackInput) => mutate({ input, messageId }),
    [mutate],
  );

  // Stable while nothing changes: only the action bars consume it.
  return useMemo(() => ({ clear, feedbackByMessageId, submit }), [clear, feedbackByMessageId, submit]);
}
